import { DIFFICULTY_RUNGS, STREAK_MILESTONES } from '../../../shared/types.ts';

export function HowItWorksView(): React.ReactElement {
  const totalWeight = DIFFICULTY_RUNGS.reduce((s, d) => s + d.weight, 0);

  return (
    <div className="how-page">
      <h2>How it works</h2>

      <div className="card how-card">
        <h3>One game, one timeline</h3>
        <p>
          witladder is a continuous, global trivia quiz. <strong>Every player on Earth sees the same question
          at the same time.</strong> A round that starts at 14:23:00 UTC ends at 14:23:20 UTC for everyone —
          there are no per-user rounds, no instances, no shards. One ladder, one game, all the time.
        </p>
        <div className="how-loop">
          <div className="how-phase">
            <span className="how-phase-tag">20s</span>
            <span className="how-phase-name">Question</span>
            <span className="muted small">Pick one of 6 answers, or sit it out.</span>
          </div>
          <span className="how-arrow" aria-hidden>→</span>
          <div className="how-phase">
            <span className="how-phase-tag">5s</span>
            <span className="how-phase-name">Reveal</span>
            <span className="muted small">Correct answer + how everyone voted.</span>
          </div>
          <span className="how-arrow" aria-hidden>→</span>
          <div className="how-phase">
            <span className="how-phase-tag">∞</span>
            <span className="how-phase-name">Next round</span>
            <span className="muted small">No waiting room. Just keep going.</span>
          </div>
        </div>
      </div>

      <div className="card how-card">
        <h3>Difficulty &amp; how often each rung shows up</h3>
        <p>
          Every question carries a difficulty rung. The picker is <strong>weighted toward the easier rungs</strong>
          {' '}so play doesn't feel like a brick wall — Brainmelters still appear, just rarely. The split is
          {' '}<strong>4·4·3·2·1</strong> from easiest to hardest, applied independently of category.
        </p>
        <div className="how-diff-table">
          {DIFFICULTY_RUNGS.map((d) => (
            <div key={d.slug} className={`how-diff-row diff-${d.slug}`}>
              <span className={`pill diff diff-${d.slug}`}>{d.name}</span>
              <div className="how-diff-bar">
                <div className="how-diff-fill" style={{ width: `${(d.weight * 100) / totalWeight}%` }} />
                <span className="how-diff-pct">{Math.round((d.weight * 100) / totalWeight)}%</span>
              </div>
              <span className="how-diff-points">
                <strong>+{d.pointsCorrect}</strong>
                <span className="muted small"> if correct</span>
              </span>
            </div>
          ))}
        </div>
        <p className="muted small how-note">
          Categories themselves are also weighted — heavier ones (General Knowledge, Geography…) come up
          a touch more often than niche ones (Philosophy, Mythology). The two weightings combine.
        </p>
      </div>

      <div className="card how-card">
        <h3>Points</h3>
        <p>
          The reward for a correct answer scales with the difficulty rung — <strong>+1 to +5</strong> base.
          Wrong and skip don't scale.
        </p>
        <table className="how-table">
          <tbody>
            <tr>
              <td><span className="pill how-result-good">Correct</span></td>
              <td><strong className="good">+1 to +5</strong></td>
              <td className="muted small">Featherweight = +1 · Brainmelter = +5. Plus any streak bonus.</td>
            </tr>
            <tr>
              <td><span className="pill how-result-bad">Wrong</span></td>
              <td><strong className="bad">−1</strong></td>
              <td className="muted small">Same penalty regardless of difficulty. Streak resets to 0.</td>
            </tr>
            <tr>
              <td><span className="pill how-result-skip">Skipped</span></td>
              <td><strong>0</strong></td>
              <td className="muted small">No penalty — but your streak still resets.</td>
            </tr>
          </tbody>
        </table>
        <p className="muted small how-note">
          Skipping costs 0 points on purpose, so you can step away without bleeding score. But the
          streak bonus does <em>not</em> survive a skip — see the next section.
        </p>
      </div>

      <div className="card how-card">
        <h3>Streaks</h3>
        <p>
          A streak is the number of correct answers you've given <strong>in a row</strong>. The moment you
          answer wrong <em>or</em> skip a round, the streak resets to 0.
        </p>
        <p className="muted small how-note">
          Skipping breaks the streak by design — otherwise you could sit out the hard ones and keep
          your run alive forever.
        </p>

        <h4>Milestone bonuses</h4>
        <p>
          Reach a streak length below and you get a one-time bonus on top of the base reward for that
          answer. Each milestone pays out <strong>once per streak run</strong> — when the streak resets,
          the next run can earn them all again.
        </p>
        <div className="streak-grid">
          {STREAK_MILESTONES.map((m) => (
            <div key={m.at} className="streak-tile">
              <div className="streak-at">{m.at}</div>
              <div className="streak-bonus">+{m.bonus}</div>
              <div className="muted small">in a row</div>
            </div>
          ))}
        </div>
        <p className="muted small how-note">
          A clean run of 100 correct in a row nets <strong>+21 milestone bonus</strong> on top of the base
          points (which themselves now depend on the difficulty mix you faced) — meaningful, but not so
          big that one good day buries the rest of the ladder.
        </p>
      </div>

      <div className="card how-card">
        <h3>Ranking</h3>
        <ul className="how-list">
          <li><strong>All time</strong> — total score across every round you've played.</li>
          <li><strong>Today</strong> / <strong>This week</strong> — score earned in the current UTC day or week.</li>
          <li><strong>Best streak</strong> — the longest correct-answer streak you've ever held.</li>
        </ul>
        <p className="muted small how-note">
          Score and streak boards can also be filtered by category and difficulty. Best-streak is global —
          a streak doesn't care which categories the questions came from.
        </p>
      </div>

      <div className="card how-card">
        <h3>Identity</h3>
        <p>
          You can play <strong>anonymously</strong> — your score, streak, and stats are stored on this browser
          and survive restarts. <strong>Sign in</strong> to keep them across devices and put your display name
          on the global ladder. Logging in <em>doesn't</em> reset your anonymous progress; it just attaches
          your existing browser identity to your account.
        </p>
      </div>

      <div className="card how-card">
        <h3>Tips</h3>
        <ul className="how-list">
          <li>Press <kbd>1</kbd>–<kbd>6</kbd> to lock in an answer with the keyboard.</li>
          <li>Once you've picked, you can't change your mind for that round.</li>
          <li>The live participant count + "answered so far" on the quiz page tick every second during the question phase.</li>
          <li>During reveal, the bars on each answer show what % of the global field picked it.</li>
        </ul>
      </div>
    </div>
  );
}
